import { useQuery } from "@tanstack/react-query"
import { createFileRoute, redirect } from "@tanstack/react-router"
import { AnimatePresence, motion } from "motion/react"

import { AuroraBackground } from "@repo/ui/components/ui/aurora-background"
import { If } from "@repo/ui/components/ui/if"
import { LoadingScreen } from "@repo/ui/components/ui/loading-screen"
import { toast } from "@repo/ui/components/ui/sonner"
import { useCountdown } from "@repo/ui/hooks/use-count-down"
import { logger } from "@repo/utils/logger"
import { getCurrentResourceQueryOptions } from "~/modules/resources/resources.queries"
import { createRolePermissionMethods } from "~/modules/roles-and-permissions/role-permission-methods"
import { OnboardingForm } from "./onboarding-form"

const log = logger.child({
  module: "resource-onboarding-route",
})

const LOAD_TRANSITION_DURATION = 1

export const Route = createFileRoute("/_protected-layout/resource-onboarding")({
  component: RouteComponent,
  beforeLoad: ({ context }) => {
    const permissionMethods = createRolePermissionMethods(
      context.appRole,
      context.rolePermissions,
    )

    const isResource = permissionMethods?.getIsResourceSystemUser()

    // If the user is not a resource or they are already onboarded, redirect to the dashboard
    if (!isResource || context.user?.app_metadata.is_onboarded) {
      throw redirect({
        to: "/dashboard",
      })
    }
  },
  loader: async ({ context }) => {
    context.queryClient
      .ensureQueryData(getCurrentResourceQueryOptions())
      .catch((error) => {
        log.error("Error getting current resource", { error })
        return null
      })
  },
})

function RouteComponent() {
  const { data: resource, isLoading } = useQuery(
    getCurrentResourceQueryOptions(),
  )

  const [count] = useCountdown({
    countStart: LOAD_TRANSITION_DURATION,
    countStop: 0,
    autoStart: true,
  })

  if (!isLoading && !resource) {
    toast.warning("Your profile is not found. Please contact support.")

    throw redirect({
      to: "/dashboard",
    })
  }

  return (
    <AnimatePresence mode="wait">
      <AuroraBackground>
        <motion.div
          className="relative z-10 flex w-full max-w-md flex-col items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div
            className="relative w-full"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{
              type: "spring",
              stiffness: 100,
              damping: 15,
            }}
          >
            <If
              condition={count <= 0 ? resource : false}
              fallback={
                <LoadingScreen
                  loadTransitionDuration={LOAD_TRANSITION_DURATION}
                />
              }
            >
              {(resource) => <OnboardingForm resource={resource} />}
            </If>
          </motion.div>
        </motion.div>
      </AuroraBackground>
    </AnimatePresence>
  )
}
